import { Typography } from 'antd'
import { sanitizeInstructions } from 'common/sanitize-html'
import { isSafeUrl } from 'common/url'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Locker } from 'types/order'

import { StyledTextSpan } from 'components/common/text'
import { newPalette } from 'components/constants/colors'

const { Text } = Typography

// ─── Disclosure toggle ────────────────────────────────────────────────────────

const StyledToggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 6px 0;
  margin-top: 12px;
  color: ${newPalette.whiteAlpha[60]};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.15s ease;
  user-select: none;

  &:hover {
    color: ${newPalette.whiteAlpha[95]};
  }
`

const StyledChevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  font-size: 10px;
  transition: transform 0.25s ease;
  transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
`

// ─── Animated content wrapper ─────────────────────────────────────────────────

const StyledReveal = styled.div<{ $open: boolean; $height: number }>`
  overflow: hidden;
  transition: height 0.3s ease, opacity 0.25s ease;
  height: ${(p) => (p.$open ? `${p.$height}px` : '0')};
  opacity: ${(p) => (p.$open ? 1 : 0)};
`

// ─── Locker entry ─────────────────────────────────────────────────────────────

const StyledLockerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
`

const StyledLockerEntry = styled.div`
  background: ${newPalette.whiteAlpha[2]};
  border: 1px solid ${newPalette.whiteAlpha[5]};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
`

const StyledLockerName = styled.div`
  color: ${newPalette.whiteAlpha[95]};
  font-weight: 500;
  font-size: 15px;
`

// ─── Section labels ───────────────────────────────────────────────────────────

const StyledSectionLabel = styled.div`
  color: ${newPalette.whiteAlpha[40]};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

// ─── License key ──────────────────────────────────────────────────────────────

const StyledLicenseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const StyledLicenseCode = styled(Text)`
  &&& {
    display: block;
    color: ${newPalette.whiteAlpha[95]};
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 13px;
    line-height: 22px;
    background: ${newPalette.panelBackground};
    border-left: 3px solid ${newPalette.blue[400]};
    padding: 14px 16px;
    border-radius: 0 8px 8px 0;
    word-break: break-all;
    white-space: pre-wrap;
    min-width: 0;
  }
`

// ─── Download button ──────────────────────────────────────────────────────────

const StyledDownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${newPalette.primary.black};
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  background: ${newPalette.primary.yellow};
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  text-decoration: none;
  width: fit-content;
  max-width: 100%;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease, opacity 0.15s ease;
  box-shadow: inset -0.5px -0.5px 1px ${newPalette.whiteAlpha[10]};

  &:hover {
    box-shadow: 0 0 12px ${newPalette.primary.yellow};
    color: ${newPalette.primary.black};
  }

  &:active {
    opacity: 0.8;
    box-shadow: none;
  }
`

// ─── Instructions ─────────────────────────────────────────────────────────────

const StyledInstructionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-top: 1px solid ${newPalette.whiteAlpha[5]};
  padding-top: 16px;
`

const StyledInstructions = styled(StyledTextSpan)`
  display: block;
  white-space: pre-wrap;
  line-height: 24px;

  a {
    color: ${newPalette.whiteAlpha[95]};
    text-decoration: underline;
  }
`

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Component ────────────────────────────────────────────────────────────────

function LockerInstructions({ html }: { html: string }): JSX.Element {
  const clean = useMemo(() => sanitizeInstructions(html), [html])
  return (
    <StyledInstructionsWrapper>
      <StyledSectionLabel>Instructions</StyledSectionLabel>
      <StyledInstructions dangerouslySetInnerHTML={{ __html: clean }} />
    </StyledInstructionsWrapper>
  )
}

interface LockersSectionProps {
  lockers: Array<Locker>
}

export function LockersSection({ lockers }: LockersSectionProps): JSX.Element | null {
  const [open, setOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)

  useEffect(() => {
    const el = contentRef.current
    if (!el || !open || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => {
      setContentHeight(el.scrollHeight)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [open])

  if (!lockers || lockers.length === 0) {
    return null
  }

  const handleToggle = () => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
    setOpen((prev) => !prev)
  }

  return (
    <div>
      <StyledToggle $open={open} onClick={handleToggle} aria-expanded={open} data-testid="locker-toggle">
        <StyledChevron $open={open}>▶︎</StyledChevron>
        Downloads &amp; licences
      </StyledToggle>

      <StyledReveal $open={open} $height={contentHeight}>
        <StyledLockerList ref={contentRef}>
          {lockers.map((locker) => (
            <StyledLockerEntry key={locker.id} data-testid="locker-entry">
              {lockers.length > 1 && <StyledLockerName>{locker.product_name}</StyledLockerName>}

              {locker.license_code && (
                <StyledLicenseWrapper>
                  <StyledSectionLabel>License key</StyledSectionLabel>
                  <StyledLicenseCode copyable={{ text: locker.license_code }} data-testid="locker-license">
                    {locker.license_code}
                  </StyledLicenseCode>
                </StyledLicenseWrapper>
              )}

              {locker.download_url && isSafeUrl(locker.download_url) && (
                <StyledDownloadLink
                  href={locker.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  referrerPolicy="no-referrer"
                >
                  Download
                </StyledDownloadLink>
              )}

              {locker.download_instructions && <LockerInstructions html={locker.download_instructions} />}
            </StyledLockerEntry>
          ))}
        </StyledLockerList>
      </StyledReveal>
    </div>
  )
}
